import { Component, inject, ViewChild } from '@angular/core';
import { ArmyBuilderConfig, HttpClientWithInFlightCache } from 'army-builder-shared';
import { firstValueFrom, Observable, switchMap } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IonInput } from '@ionic/angular';
import { ToastService } from '../../../global/toast/toast';

@Component({
    standalone: false,
    selector: 'users-page',
    template: `
        <abs-page-layout title="Users">
            <div class="content">
                <form [formGroup]="formGroup">
                    <div class="form-group">
                        <ion-label>Find User by Email</ion-label>
                        <ion-input (keyup.enter)="onEnter($event)" [formControlName]="'email'"></ion-input>
                        <span class="error" *ngIf="formGroup.errors?.length > 0"> Required</span>
                    </div>
                    <div class="buttons">
                        <ion-button (click)="submit()">Search</ion-button>
                    </div>
                </form>
            </div>

            <ng-template #rowTemplate let-item="item">
                <div class="itemWrap">{{ item.email }}</div>
            </ng-template>

            <div *ngIf="user$ | async as user">
                <p>
                    <a [href]="'https://app.revenuecat.com/customers/' + appConfig.revenueCatAppId + '/' + user._id" target="_blank"
                        >View in RevenueCat</a
                    >
                </p>

                <h2>Roles</h2>
                <p>
                    Most roles should be granted by RevenueCat, but for roles not linked to purchases (e.g. admin or editor roles), they can
                    be added here. Roles take effect on the next login. To add multiple roles, separate with commas.
                </p>
                <ion-item>
                    <ion-input placeholder="(Role name)" #roleName></ion-input>
                    <ion-button slot="end" (click)="addRole(user)">+</ion-button>
                </ion-item>
                <ion-list>
                    <ion-item *ngFor="let role of user.metadata?.roles">
                        {{ role }}
                        <ion-icon name="close" slot="end" (click)="removeRole(role)"></ion-icon>
                    </ion-item>
                </ion-list>

                <p><ion-button (click)="copyUserRoles(user.metadata?.roles)">Copy user roles to clipboard</ion-button></p>

                <h2>User Object</h2>
                <pre>{{ user | json }}</pre>
                <h2>Entitlements</h2>
                <pre>{{ userEntitlements$ | async | json }}</pre>
            </div>
        </abs-page-layout>
    `
})
export class UserLookupPage {
    usersUrl = this.appConfig.apiBaseUrl + '/user/lookup/';
    entitlementsUrl = this.appConfig.apiBaseUrl + '/user/entitlements/';
    rolesUrl = this.appConfig.apiBaseUrl + '/user/roles/';
    user$: Observable<any>;
    userEntitlements$: Observable<any>;
    toastService = inject(ToastService);

    @ViewChild('roleName')
    roleName: IonInput;

    constructor(
        private fb: FormBuilder,
        private httpClient: HttpClientWithInFlightCache,
        protected appConfig: ArmyBuilderConfig
    ) {}

    formGroup: FormGroup = this.fb.group({
        email: this.fb.control('', Validators.required)
    });

    async submit() {
        if (this.formGroup.errors?.length > 0) {
            return;
        }

        const { email } = this.formGroup.value;

        // await firstValueFrom(this.httpClient.post(this.usersUrl, { email }));
        this.lookupUpUser(email);
    }

    lookupUpUser(email: string) {
        this.user$ = this.httpClient.get(this.usersUrl + email, {
            headers: { ...this.appConfig.globalRequestHeaders },
            withCredentials: true,
            requiresLogin: true
        });

        this.userEntitlements$ = this.user$.pipe(
            switchMap((user) =>
                this.httpClient.get(this.entitlementsUrl + user._id, {
                    headers: { ...this.appConfig.globalRequestHeaders },
                    withCredentials: true,
                    requiresLogin: true
                })
            )
        );
    }

    onEnter(e: any) {
        this.lookupUpUser(e.target.value);
    }

    async addRole(user: any) {
        if (this.roleName.value) {
            await firstValueFrom(this.httpClient.post(this.rolesUrl + user.email, { roles: this.roleName.value }));
            this.lookupUpUser(user.email);
        }
    }

    async removeRole(role) {
        let user = await firstValueFrom(this.user$);
        await firstValueFrom(this.httpClient.delete(this.rolesUrl + user.email + '/' + role));
        this.lookupUpUser(user.email);
    }

    copyUserRoles(roles) {
        navigator.clipboard.writeText(JSON.stringify(roles));
        this.toastService.showToast('', 'Copied user roles to clipboard', { color: 'success', duration: 3000 });
    }
}
